import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Shot, type ShotName } from './Shot';
import styles from './ViewSwitch.module.css';

const VIEWS = [
  { id: 'table', shot: 'database' },
  { id: 'cards', shot: 'database-cards' },
  { id: 'kanban', shot: 'database-kanban' },
] as const satisfies readonly { id: string; shot: ShotName }[];

/**
 * The same five database records as a table, as cards and as a kanban board: three real pictures
 * of the application (one example dataset, shot with each mode picked explicitly) and a plain
 * three-button switch. The picture is keyed by the chosen view, so it is replaced - not just the
 * pressed button - and nothing moves: the frame has one fixed aspect ratio for all three.
 */
export function ViewSwitch() {
  const { t } = useTranslation();
  const [view, setView] = useState<(typeof VIEWS)[number]['id']>('table');
  const current = VIEWS.find((item) => item.id === view) ?? VIEWS[0];
  return (
    <div className={styles.root}>
      <div className={styles.switch} role="group" aria-label={t('landing.shots.switch')}>
        {VIEWS.map((item) => (
          <button
            key={item.id}
            type="button"
            aria-pressed={view === item.id}
            data-view={item.id}
            onClick={() => {
              setView(item.id);
            }}
          >
            {t(`landing.views.${item.id}`)}
          </button>
        ))}
      </div>
      <figure
        className={`${styles.frame} scroll-strip`}
        tabIndex={0}
        aria-label={t(`landing.views.${view}`)}
        data-testid="database-showcase"
        data-view={view}
      >
        <Shot key={current.shot} name={current.shot} />
      </figure>
    </div>
  );
}
